import { Injectable } from '@angular/core';
import { RoomService } from './room.service';

@Injectable({
  providedIn: 'root',
})
export class CallAudioService {
  private peer?: RTCPeerConnection;
  private localStream?: MediaStream;
  private startPromise?: Promise<void>;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private callId = 0;
  private otherUserId = 0;
  private role: 'caller' | 'receiver' = 'caller';
  private remoteAudio = new Audio();

  constructor(private roomService: RoomService) {
    this.remoteAudio.autoplay = true;
    this.roomService.callSignal$.subscribe((signal) =>
      this.handleSignal(signal.callId, signal.fromUserId, signal.type, signal.payload)
    );
  }

  start(callId: number, otherUserId: number, role: 'caller' | 'receiver'): Promise<void> {
    this.callId = callId;
    this.otherUserId = otherUserId;
    this.role = role;
    this.startPromise = this.createConnection();
    return this.startPromise;
  }

  stop(): void {
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.peer?.close();
    this.peer = undefined;
    this.localStream = undefined;
    this.startPromise = undefined;
    this.pendingCandidates = [];
    this.remoteAudio.srcObject = null;
  }

  private async createConnection(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const peer = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });

    stream.getTracks().forEach((track) => peer.addTrack(track, stream));

    peer.ontrack = (event) => {
      this.remoteAudio.srcObject = event.streams[0];
    };

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        this.roomService.sendCallSignal(this.callId, this.otherUserId, 'ice', JSON.stringify(event.candidate));
      }
    };

    this.localStream = stream;
    this.peer = peer;
  }

  private async handleSignal(callId: number, fromUserId: number, type: string, payload: string): Promise<void> {
    if (callId !== this.callId || fromUserId !== this.otherUserId) return;

    await this.startPromise;
    const peer = this.peer;
    if (!peer) return;

    if (type === 'ready' && this.role === 'caller') {
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      await this.roomService.sendCallSignal(this.callId, this.otherUserId, 'offer', JSON.stringify(offer));
    } else if (type === 'offer' && this.role === 'receiver') {
      await peer.setRemoteDescription(JSON.parse(payload));
      await this.flushCandidates();
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      await this.roomService.sendCallSignal(this.callId, this.otherUserId, 'answer', JSON.stringify(answer));
    } else if (type === 'answer' && this.role === 'caller') {
      await peer.setRemoteDescription(JSON.parse(payload));
      await this.flushCandidates();
    } else if (type === 'ice') {
      const candidate: RTCIceCandidateInit = JSON.parse(payload);
      if (peer.remoteDescription) {
        await peer.addIceCandidate(candidate);
      } else {
        this.pendingCandidates.push(candidate);
      }
    }
  }

  private async flushCandidates(): Promise<void> {
    for (const candidate of this.pendingCandidates) {
      await this.peer?.addIceCandidate(candidate);
    }
    this.pendingCandidates = [];
  }
}
